/**
 * Wraps navigator.geolocation.watchPosition and normalizes into Fix, deriving
 * SOG/COG in packages/core when the browser doesn't supply them (spec 02).
 */
import { MotionDeriver, cogIsMeaningful } from '@boatnav/core';
import { posStore, type Fix } from './posStore.js';

const STALE_MS = 5_000;
const LOST_MS = 15_000;

let watchId: number | null = null;
let staleTimer: ReturnType<typeof setInterval> | null = null;
const deriver = new MotionDeriver();

function msToKn(mps: number): number {
  return (mps * 3600) / 1852;
}

function onPosition(pos: GeolocationPosition): void {
  const { latitude, longitude, accuracy, speed, heading } = pos.coords;
  const ts = pos.timestamp;

  // Prefer browser-supplied motion; derive when null (old devices / low speed).
  const derived = deriver.push({ lat: latitude, lon: longitude, accuracyM: accuracy, ts });
  let sogKn = speed != null && !Number.isNaN(speed) ? msToKn(speed) : derived.sogKn;
  let cogDeg = heading != null && !Number.isNaN(heading) ? heading : derived.cogDeg;

  // COG is meaningless at rest.
  if (!cogIsMeaningful(sogKn)) cogDeg = null;
  if (sogKn != null && sogKn < 0) sogKn = 0;

  const fix: Fix = { lat: latitude, lon: longitude, accuracyM: accuracy, sogKn, cogDeg, ts };
  posStore.getState().setFix(fix);
  posStore.getState().setStatus('ok');
}

function onError(err: GeolocationPositionError): void {
  if (err.code === err.PERMISSION_DENIED) {
    posStore.getState().setStatus('denied');
  } else {
    posStore.getState().setStatus('unavailable');
  }
}

function checkStaleness(): void {
  const { fix, status, setStatus } = posStore.getState();
  if (!fix || status === 'denied' || status === 'unavailable') return;
  const age = Date.now() - fix.ts;
  if (age > LOST_MS) setStatus('lost');
  else if (age > STALE_MS) setStatus('stale');
  else setStatus('ok');
}

export function startPositionWatch(): void {
  if (!('geolocation' in navigator)) {
    posStore.getState().setStatus('unavailable');
    return;
  }
  if (watchId != null) return;
  deriver.reset();
  posStore.getState().setStatus('acquiring');
  watchId = navigator.geolocation.watchPosition(onPosition, onError, {
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 30_000,
  });
  staleTimer = setInterval(checkStaleness, 1_000);
}

export function stopPositionWatch(): void {
  if (watchId != null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  if (staleTimer != null) {
    clearInterval(staleTimer);
    staleTimer = null;
  }
}
